import { useReveal } from "@/hooks/useReveal";

const SIGNALS = [
  { code: "01", title: "Rising dwell", desc: "Congestion debt accumulating upstream" },
  { code: "02", title: "Empty miles", desc: "Failed load pairing or poor trailer positioning" },
  { code: "03", title: "Detention exposure", desc: "Schedule integrity breakdown" },
  { code: "04", title: "Forecast error", desc: "Demand signal distortion before execution failure" },
  { code: "05", title: "Expedited freight", desc: "Planning failure converted into premium transportation cost" },
  { code: "06", title: "EDI/API gaps", desc: "Visibility failure inside the digital nervous system" },
  { code: "07", title: "Cost-to-serve leakage", desc: "Misalignment between operational behavior and financial reporting" },
];

export default function ProblemSection() {
  const { ref, visible } = useReveal();

  return (
    <section
      id="problem"
      ref={ref}
      data-testid="problem-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="absolute inset-0 bg-grid-fine opacity-40 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2 mb-5">
              <span className="num-label">SECTION 02 / PROBLEM</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <h2
              data-testid="problem-headline"
              className={`font-display font-bold text-axiom-text tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] reveal ${
                visible ? "in" : ""
              }`}
            >
              The Cost <br />
              <span className="text-axiom-muted">Does Not</span> <br />
              Disappear. <br />
              <span className="text-axiom-accent">It Moves.</span>
            </h2>
          </div>

          <div className="lg:col-span-7">
            <p
              className={`text-axiom-muted text-base sm:text-lg leading-relaxed max-w-2xl reveal ${
                visible ? "in" : ""
              }`}
              style={{ transitionDelay: "80ms" }}
            >
              Operational cost often travels through the system before it
              appears in finance. A dwell issue becomes trailer imbalance.
              Trailer imbalance becomes empty miles. Empty miles become capacity
              loss. Capacity loss becomes service failure. Service failure
              becomes financial leakage.
            </p>

            <div className="mt-10 border border-white/10 divide-y divide-white/10">
              {SIGNALS.map((s, i) => (
                <div
                  key={s.code}
                  data-testid={`problem-signal-${s.code}`}
                  className={`grid grid-cols-12 items-center px-5 py-4 hover:bg-white/[0.02] transition-colors duration-200 group reveal ${
                    visible ? "in" : ""
                  }`}
                  style={{ transitionDelay: `${120 + i * 50}ms` }}
                >
                  <div className="col-span-1 num-label">{s.code}</div>
                  <div className="col-span-4 sm:col-span-3 font-display text-axiom-text font-medium text-base">
                    {s.title}
                  </div>
                  <div className="col-span-7 sm:col-span-7 text-sm text-axiom-muted leading-snug">
                    {s.desc}
                  </div>
                  <div className="col-span-12 sm:col-span-1 flex justify-end mt-2 sm:mt-0">
                    <span className="signal-dot opacity-50 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              ))}
            </div>

            <div
              className={`mt-10 flex items-center gap-3 reveal ${visible ? "in" : ""}`}
              style={{ transitionDelay: "500ms" }}
            >
              <div className="signal-dot animate-pulse-signal" />
              <p
                data-testid="problem-closing-line"
                className="font-display text-lg md:text-xl text-axiom-text"
              >
                Axiom-AI maps the movement of cost through the system.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
